// The PIN to leave Kitchen or Counter (docs/plans/stations.md). It keeps staff out of the rest of
// the app on a shared tablet; it is not security, so it stays on this device, salted and hashed.
const ROUNDS = 100_000
const MAX_TRIES = 5
export const PIN_WAIT_MS = 30_000
export const PIN_LENGTH = 4

interface Saved {
  salt: string
  hash: string
}
interface Tries {
  wrong: number
  until: number
}

const pinKey = (userId: string, tenantId: string) => `ims.station-pin.${userId}.${tenantId}`
const triesKey = (userId: string, tenantId: string) => `ims.station-tries.${userId}.${tenantId}`
const hex = (bytes: Uint8Array) => [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')

async function hashPin(pin: string, salt: string): Promise<string> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(pin),
    'PBKDF2',
    false,
    ['deriveBits'],
  )
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: new TextEncoder().encode(salt), iterations: ROUNDS },
    material,
    256,
  )
  return hex(new Uint8Array(bits))
}

const read = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export const hasPin = (userId: string, tenantId: string) =>
  read<Saved>(pinKey(userId, tenantId)) !== null

export async function savePin(userId: string, tenantId: string, pin: string): Promise<void> {
  const salt = hex(crypto.getRandomValues(new Uint8Array(16)))
  const saved: Saved = { salt, hash: await hashPin(pin, salt) }
  localStorage.setItem(pinKey(userId, tenantId), JSON.stringify(saved))
  localStorage.removeItem(triesKey(userId, tenantId))
}

export function clearPin(userId: string, tenantId: string) {
  localStorage.removeItem(pinKey(userId, tenantId))
  localStorage.removeItem(triesKey(userId, tenantId))
}

/** Milliseconds until another try is allowed; 0 when one is. */
export function pinWait(userId: string, tenantId: string, now = Date.now()): number {
  const tries = read<Tries>(triesKey(userId, tenantId))
  return tries ? Math.max(0, tries.until - now) : 0
}

/** True when the PIN is right. A wrong one counts, and the fifth starts the wait. */
export async function checkPin(
  userId: string,
  tenantId: string,
  pin: string,
  now = Date.now(),
): Promise<boolean> {
  const saved = read<Saved>(pinKey(userId, tenantId))
  if (!saved || pinWait(userId, tenantId, now) > 0) return false
  if ((await hashPin(pin, saved.salt)) === saved.hash) {
    localStorage.removeItem(triesKey(userId, tenantId))
    return true
  }
  const wrong = (read<Tries>(triesKey(userId, tenantId))?.wrong ?? 0) + 1
  const tries: Tries =
    wrong >= MAX_TRIES ? { wrong: 0, until: now + PIN_WAIT_MS } : { wrong, until: 0 }
  localStorage.setItem(triesKey(userId, tenantId), JSON.stringify(tries))
  return false
}
